import { Injectable, inject } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import { DeviceData, DeviceType, DeviceSize } from '../data/device.data';

@Injectable({
    providedIn: 'root',
})
export class DeviceService {
    private breakpointObserver = inject(BreakpointObserver);

    private _deviceType$ = new BehaviorSubject<DeviceType>(DeviceType.DESKTOP);
    private _deviceSize$ = new BehaviorSubject<DeviceSize>(DeviceSize.LG);

    deviceType$ = this._deviceType$.asObservable();
    deviceSize$ = this._deviceSize$.asObservable();

    constructor() {
        const { md, lg, xl, xxl } = DeviceData.breakpoints;

        const queries = {
            [DeviceSize.SM]: `(max-width: ${md - 1}px)`,
            [DeviceSize.MD]: `(min-width: ${md}px) and (max-width: ${lg - 1}px)`,
            [DeviceSize.LG]: `(min-width: ${lg}px) and (max-width: ${xl - 1}px)`,
            [DeviceSize.XL]: `(min-width: ${xl}px) and (max-width: ${xxl - 1}px)`,
            [DeviceSize.XXL]: `(min-width: ${xxl}px)`,
        };

        this.breakpointObserver
            .observe(Object.values(queries))
            .pipe(
                map((state: BreakpointState) => {
                    if (state.breakpoints[queries[DeviceSize.SM]]) return DeviceSize.SM;
                    if (state.breakpoints[queries[DeviceSize.MD]]) return DeviceSize.MD;
                    if (state.breakpoints[queries[DeviceSize.LG]]) return DeviceSize.LG;
                    if (state.breakpoints[queries[DeviceSize.XL]]) return DeviceSize.XL;
                    if (state.breakpoints[queries[DeviceSize.XXL]]) return DeviceSize.XXL;

                    return DeviceSize.LG;
                }),
                distinctUntilChanged()
            )
            .subscribe((size) => this._deviceSize$.next(size));

        this.breakpointObserver
            .observe(`(max-width: ${md}px)`) // SM and MD are considered MOBILE
            .pipe(
                map((result) => (result.matches ? DeviceType.MOBILE : DeviceType.DESKTOP)),
                distinctUntilChanged()
            )
            .subscribe((deviceType) => this._deviceType$.next(deviceType));
    }

    get currentDeviceType(): DeviceType {
        return this._deviceType$.getValue();
    }

    get currentDeviceSize(): DeviceSize {
        return this._deviceSize$.getValue();
    }
}
